import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';

const RefundPolicy = () => {
  return (
    <>
      <SEO
        title="Refund & Cancellation Policy"
        description="Theatre booking platform refund and cancellation policy - guidelines for cancellations, refunds, and rescheduling."
        keywords={['refund policy', 'cancellation policy', 'theatre booking', 'booking changes']}
        url="/refund-policy"
      />
      
      <PageHero
        title="Refund & Cancellation Policy"
        subtitle="Guidelines for cancellations, refunds, and rescheduling"
        image="/gallery/image.jpg"
      />
      
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-md">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="prose prose-lg max-w-none"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Refund & Cancellation Policy</h2>
              
              <p className="mb-6 text-gray-700">
                Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
              
              <p className="mb-4 text-gray-700">
                We understand that plans can change. This Refund and Cancellation Policy outlines the terms and conditions regarding cancellations, refunds, and rescheduling of bookings made through our theatre booking platform. By making a booking through our Services, you agree to the following policy.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Standard Cancellation Policy</h3>
              
              <p className="mb-4 text-gray-700">
                Our standard cancellation policy is as follows:
              </p>
              
              <div className="overflow-hidden bg-white shadow sm:rounded-lg mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cancellation Timeframe</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refund Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">48+ hours before booking time</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">100% refund</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">24-48 hours before booking time</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-medium">50% refund</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">Less than 24 hours before booking time</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">No refund</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">No-show</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">No refund</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="mb-4 text-gray-700">
                All times mentioned above are in relation to the local time at the venue location. The cancellation time is determined by the timestamp when the cancellation request is confirmed in our system.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. Venue-Specific Policies</h3>
              
              <p className="mb-4 text-gray-700">
                Some venues may have their own specific cancellation and refund policies that override our standard policy. In such cases, the venue's policy will be clearly displayed during the booking process. By proceeding with the booking, you agree to the venue's specific cancellation and refund terms.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. How to Cancel a Booking</h3>
              
              <p className="mb-4 text-gray-700">
                You can cancel a booking by:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Logging into your account and navigating to "My Bookings"</li>
                <li>Selecting the booking you wish to cancel and clicking the "Cancel Booking" button</li>
                <li>Following the prompts to confirm the cancellation</li>
                <li>Alternatively, you can contact our customer support team at <a href="mailto:habbaeventsblr@gmail.com" className="text-indigo-600 hover:text-indigo-500">habbaeventsblr@gmail.com</a> or <a href="tel:+918722479114" className="text-indigo-600 hover:text-indigo-500">+918970144660</a></li>
              </ul>
              
              <p className="mb-4 text-gray-700">
                A cancellation confirmation will be sent to your registered email address once the cancellation is processed.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Refund Process</h3>
              
              <p className="mb-4 text-gray-700">
                When you are eligible for a refund, the following process applies:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Refunds will be processed and credited to the original payment method used for the booking</li>
                <li>It may take an additional 5-7 business days for the refund to appear in your account, depending on your payment provider or bank</li>
                <li>For Cash on Delivery (COD) bookings, only the advance payment will be refunded according to the above policy</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Rescheduling Policy</h3>
              
              <p className="mb-4 text-gray-700">
                Instead of canceling, you may choose to reschedule your booking, subject to the following conditions:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Rescheduling requests must be made at least 24 hours before the original booking time</li>
                <li>You can reschedule once without incurring any additional charges</li>
                <li>The new booking date must be within 30 days of the original booking date</li>
                <li>Rescheduling is subject to venue availability</li>
                <li>If the price for the new time slot is higher, you will need to pay the difference</li>
                <li>If the price for the new time slot is lower, the difference will be credited to your account for future bookings</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Special Circumstances</h3>
              
              <p className="mb-4 text-gray-700">
                In the following special circumstances, we may provide a full refund regardless of the standard cancellation timeframes:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Venue closure due to unforeseen circumstances</li>
                <li>Natural disasters or severe weather conditions affecting the venue</li>
                <li>Government restrictions or mandates preventing the venue from operating</li>
                <li>Technical failures on our platform that resulted in booking errors</li>
              </ul>
              
              <p className="mb-4 text-gray-700">
                In such cases, we will make reasonable efforts to notify you as soon as possible and process the refund automatically.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Promotional Discounts and Coupons</h3>
              
              <p className="mb-4 text-gray-700">
                For bookings made using promotional discounts or coupons:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>The refund amount will be based on the actual amount paid (after applying the discount)</li>
                <li>One-time use coupons or promotions will not be reissued if a booking is canceled</li>
                <li>For bookings where "store credit" or "account balance" was used, the refunded amount will be credited back to your account balance</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Contact Us</h3>
              
              <p className="mb-4 text-gray-700">
                If you have any questions or concerns about our Refund and Cancellation Policy, please contact our customer support team at:
              </p>
              
              <div className="mb-6 text-gray-700">
                <p>Email: <a href="mailto:habbaeventsblr@gmail.com" className="text-indigo-600 hover:text-indigo-500">habbaeventsblr@gmail.com</a></p>
                <p>Phone: <a href="tel:+918722479114" className="text-indigo-600 hover:text-indigo-500">+918970144660</a></p>
                <p>Address: Abhay mansion No 103&104 Devasandra main road Near venugopal swamy temple KR Puram Bangalore 560036</p>
              </div>
              
              <p className="mb-4 text-gray-700">
                We reserve the right to modify this policy at any time. Any changes will be effective immediately upon posting the updated policy on our website.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundPolicy; 